import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Plus, Users, MapPin, Wifi, CalendarDays, Search, Sparkles, Ticket,
  LayoutGrid, Calendar as CalendarIcon, ChevronLeft, ChevronRight,
} from "lucide-react";

const SKILL_LABEL = {
  all_levels: "All levels",
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
};

/**
 * Local Meetups feed — public in-real-life gatherings organised by Hoomans.
 * Filters mirror Jobs/Collabs boards; upcoming meetups are shown by default.
 * Users can toggle between Grid view (list of cards) and Month calendar view.
 */
export default function Meetups() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState({ categories: [], skill_levels: [] });
  const [cat, setCat] = useState("");
  const [sk, setSk] = useState("");
  const [paid, setPaid] = useState("");
  const [q, setQ] = useState("");
  const [view, setView] = useState("grid");           // grid | calendar

  useEffect(() => {
    api.get("/meetups/categories").then(r => setMeta(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    const p = new URLSearchParams();
    if (cat) p.set("category", cat);
    if (sk) p.set("skill_level", sk);
    if (paid !== "") p.set("is_paid", paid);
    if (q) p.set("q", q);
    api.get(`/meetups?${p.toString()}`)
      .then(r => setItems(r.data || []))
      .catch(e => toast.error(formatErr(e)));
  }, [cat, sk, paid, q]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="meetups-page">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <p className="overline">Local Meetups</p>
          <h1 className="h-display text-4xl">Meet Hoomans IRL.</h1>
          <p className="text-[#4A4A4A] mt-1">Discover local gatherings around your interests, profession, and passions.</p>
        </div>
        {user && (
          <Link to="/meetups/new" className="btn-primary" data-testid="meetups-new-btn">
            <Plus size={14}/> Host a meetup
          </Link>
        )}
      </div>

      <div className="mt-6 grid sm:grid-cols-[1fr_auto_auto_auto] gap-2 items-center" data-testid="meetups-filters">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A] pointer-events-none"/>
          <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search title, description, tags…"
                 className="input !pl-9" data-testid="meetups-search"/>
        </div>
        <select className="input" value={cat} onChange={(e)=>setCat(e.target.value)} data-testid="meetups-category">
          <option value="">All categories</option>
          {meta.categories?.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="input" value={sk} onChange={(e)=>setSk(e.target.value)} data-testid="meetups-skill">
          <option value="">Any skill</option>
          {(meta.skill_levels || []).map(l => <option key={l} value={l}>{SKILL_LABEL[l] || l}</option>)}
        </select>
        <div className="flex gap-1" data-testid="meetups-paid-toggle">
          {[["", "Any"], ["false", "Free"], ["true", "Paid"]].map(([v, l]) => (
            <button key={v || "any"} type="button" onClick={()=>setPaid(v)}
              data-testid={`meetups-paid-${v || "any"}`}
              className={`text-xs px-3 py-2 rounded-full ${paid===v ? "bg-[#0A0A0A] text-white" : "border border-[#E5E5E3] text-[#4A4A4A] hover:bg-[#F2F2F0]"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* View toggle: grid vs calendar */}
      <div className="mt-5 inline-flex rounded-full border border-[#E5E5E3] p-0.5" data-testid="meetups-view-toggle">
        <button type="button" onClick={()=>setView("grid")}
          data-testid="meetups-view-grid"
          className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 ${view==="grid" ? "bg-[#0A0A0A] text-white" : "text-[#4A4A4A] hover:bg-[#F2F2F0]"}`}>
          <LayoutGrid size={12}/> Grid
        </button>
        <button type="button" onClick={()=>setView("calendar")}
          data-testid="meetups-view-calendar"
          className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 ${view==="calendar" ? "bg-[#0A0A0A] text-white" : "text-[#4A4A4A] hover:bg-[#F2F2F0]"}`}>
          <CalendarIcon size={12}/> Calendar
        </button>
      </div>

      {items === null && <p className="text-[#8A8A8A] mt-8">Loading…</p>}
      {items && items.length === 0 && (
        <div className="card p-12 text-center mt-8" data-testid="meetups-empty">
          <Sparkles size={22} className="mx-auto text-[#0057FF] mb-2"/>
          <p className="text-[#4A4A4A]">No upcoming meetups match your filters. Be the first to host one.</p>
          {user && <Link to="/meetups/new" className="btn-secondary inline-flex mt-3">Host a meetup</Link>}
        </div>
      )}

      {items && items.length > 0 && view === "grid" && (
        <ul className="grid sm:grid-cols-2 gap-3 mt-6" data-testid="meetups-list">
          {items.map(m => <MeetupCard key={m.id} m={m}/>)}
        </ul>
      )}

      {items && items.length > 0 && view === "calendar" && (
        <MeetupCalendar items={items}/>
      )}
    </div>
  );
}

function MeetupCard({ m }) {
  const dt = new Date(m.starts_at);
  const dateStr = isNaN(dt.getTime()) ? m.starts_at : dt.toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
  return (
    <li className="card p-0 overflow-hidden hover:border-[#0A0A0A] transition-colors" data-testid={`meetup-card-${m.id}`}>
      <Link to={`/meetups/${m.id}`} className="block">
        {m.image ? (
          <div className="aspect-[16/9] bg-[#F2F2F0]"><img src={m.image} alt="" className="w-full h-full object-cover"/></div>
        ) : (
          <div className="aspect-[16/9] bg-gradient-to-br from-[#F2F2F0] to-[#E8E8E6] flex items-center justify-center">
            <CalendarDays size={28} className="text-[#8A8A8A]"/>
          </div>
        )}
        <div className="p-4">
          <div className="flex items-center gap-2 flex-wrap text-[11px] text-[#8A8A8A]">
            <span className="badge-soft">{m.category}</span>
            <span>· {SKILL_LABEL[m.skill_level] || m.skill_level}</span>
            {m.is_paid ? (
              <span className="inline-flex items-center gap-0.5 text-[#0057FF] font-medium">
                <Ticket size={11}/> {m.currency} {Number(m.price).toFixed(0)}
              </span>
            ) : (
              <span className="text-[#22883E] font-medium">Free</span>
            )}
          </div>
          <p className="text-[15px] font-bold text-[#0A0A0A] mt-1.5 line-clamp-2" data-testid={`meetup-title-${m.id}`}>{m.title}</p>
          <p className="text-xs text-[#4A4A4A] mt-1.5 inline-flex items-center gap-1 flex-wrap">
            <CalendarDays size={11}/> {dateStr}
          </p>
          <p className="text-xs text-[#4A4A4A] mt-1 inline-flex items-center gap-1 flex-wrap">
            {m.lat != null ? <MapPin size={11}/> : <Wifi size={11}/>}
            <span className="truncate">{m.location || "Location TBA"}</span>
          </p>
          <p className="text-xs text-[#4A4A4A] mt-2 inline-flex items-center gap-1">
            <Users size={11}/> {m.attendee_count || 0}{m.max_attendees ? ` / ${m.max_attendees}` : ""} going
            {m.owner?.name && <span className="text-[#8A8A8A]"> · hosted by {m.owner.name}</span>}
          </p>
        </div>
      </Link>
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* Month calendar — buckets meetups by day and lets you jump month-to-month */
/* ------------------------------------------------------------------ */
function MeetupCalendar({ items }) {
  // Anchor month = the month of the first (earliest) upcoming meetup, fallback today
  const initial = useMemo(() => {
    const first = items[0]?.starts_at ? new Date(items[0].starts_at) : new Date();
    return new Date(first.getFullYear(), first.getMonth(), 1);
  }, [items]);
  const [anchor, setAnchor] = useState(initial);

  const monthName = anchor.toLocaleString(undefined, { month: "long", year: "numeric" });
  const daysInMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
  const firstWeekday = new Date(anchor.getFullYear(), anchor.getMonth(), 1).getDay(); // 0 = Sun

  // Bucket meetups by YYYY-MM-DD in local time
  const byDay = useMemo(() => {
    const map = {};
    for (const m of items) {
      const d = new Date(m.starts_at);
      if (isNaN(d.getTime())) continue;
      if (d.getFullYear() !== anchor.getFullYear() || d.getMonth() !== anchor.getMonth()) continue;
      const key = d.getDate();
      if (!map[key]) map[key] = [];
      map[key].push(m);
    }
    return map;
  }, [items, anchor]);

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const shift = (delta) => {
    setAnchor(a => new Date(a.getFullYear(), a.getMonth() + delta, 1));
  };
  const today = new Date();
  const isToday = (d) => d === today.getDate() && anchor.getMonth() === today.getMonth() && anchor.getFullYear() === today.getFullYear();

  return (
    <div className="mt-6" data-testid="meetups-calendar">
      <div className="flex items-center justify-between mb-3">
        <div className="inline-flex items-center gap-2">
          <button type="button" onClick={()=>shift(-1)}
            data-testid="meetups-cal-prev"
            className="h-8 w-8 rounded-full border border-[#E5E5E3] hover:bg-[#F2F2F0] inline-flex items-center justify-center">
            <ChevronLeft size={14}/>
          </button>
          <p className="text-sm font-medium" data-testid="meetups-cal-title">{monthName}</p>
          <button type="button" onClick={()=>shift(1)}
            data-testid="meetups-cal-next"
            className="h-8 w-8 rounded-full border border-[#E5E5E3] hover:bg-[#F2F2F0] inline-flex items-center justify-center">
            <ChevronRight size={14}/>
          </button>
        </div>
        <button type="button" onClick={()=>setAnchor(new Date(today.getFullYear(), today.getMonth(), 1))}
          className="btn-ghost text-xs" data-testid="meetups-cal-today">Today</button>
      </div>

      <div className="grid grid-cols-7 gap-px bg-[#E5E5E3] border border-[#E5E5E3] rounded-2xl overflow-hidden">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
          <div key={d} className="bg-[#F8F8F6] py-2 text-[11px] uppercase tracking-wider text-[#8A8A8A] text-center">{d}</div>
        ))}
        {cells.map((d, idx) => (
          <div key={idx} className={`bg-white min-h-[104px] p-2 text-left ${d==null ? "bg-[#FAFAFA]" : ""}`}>
            {d != null && (
              <>
                <div className={`text-[11px] font-medium inline-flex items-center justify-center h-5 min-w-5 px-1 rounded-full ${isToday(d) ? "bg-[#0057FF] text-white" : "text-[#4A4A4A]"}`}>
                  {d}
                </div>
                <div className="mt-1 space-y-1">
                  {(byDay[d] || []).slice(0, 3).map(m => (
                    <Link key={m.id} to={`/meetups/${m.id}`}
                      data-testid={`meetups-cal-item-${m.id}`}
                      className="block truncate text-[11px] px-1.5 py-0.5 rounded-md bg-[#F2F2F0] hover:bg-[#0057FF] hover:text-white transition-colors">
                      {new Date(m.starts_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })} · {m.title}
                    </Link>
                  ))}
                  {(byDay[d] || []).length > 3 && (
                    <p className="text-[10px] text-[#8A8A8A] px-1.5">+{byDay[d].length - 3} more</p>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

